import type { SpinScopeStatus } from '../../Generated/SpinScopeStatus.ts'

export type SpinStatusKind = 'available' | 'pending' | 'none'
export type SpinStatusScope = 'ORDER' | 'PROJECT'

export interface SpinStatusDescription {
  kind: SpinStatusKind
  /** Short badge text; null for `none`, which shows no label. */
  title: string | null
  /** What follows the title in a label; null when the title says it all. */
  detail: string | null
  /** The full sentence, for tooltips and empty states. */
  text: string
  /** Display names of the orders still waiting for clustering. */
  pendingOrders: string[]
}

const NAMED_PENDING_ORDERS = 2
const NEXT_RUN = 'they will be available after the next nightly clustering run'

const orderLabel = (order: { orderId: string; orderName?: string }): string => order.orderName || order.orderId.slice(0, 8)

const pendingOrderList = (names: string[]): string => {
  const shown = names.slice(0, NAMED_PENDING_ORDERS).join(', ')
  const rest = names.length - NAMED_PENDING_ORDERS
  return rest > 0 ? `${shown} +${rest} more` : shown
}

/**
 * The single source of every SPIN availability text. `status` is the `status` of the `/spin/{scopeId}/state` answer — undefined
 * while loading or after a failed fetch, which reads as `none`. Only a project names its pending orders; on an order
 * the one row is the order itself.
 */
export const describeSpinStatus = (
  status: SpinScopeStatus | null | undefined,
  scope: SpinStatusScope
): SpinStatusDescription => {
  const orders = status?.orders ?? []
  const pending = orders.filter((o) => !o.spinsAvailable)
  if (orders.length === 0) {
    const noun = scope === 'ORDER' ? 'order' : 'project'
    return {
      kind: 'none',
      title: null,
      detail: null,
      text: `No SPINs for this ${noun} yet. SPINs are clustered nightly once a report is available.`,
      pendingOrders: []
    }
  }
  if (pending.length === 0) {
    return { kind: 'available', title: 'SPINs available', detail: null, text: 'SPINs available', pendingOrders: [] }
  }
  const pendingOrders = pending.map(orderLabel)
  const detail = scope === 'PROJECT' ? `for ${pendingOrderList(pendingOrders)} — ${NEXT_RUN}.` : `— ${NEXT_RUN}.`
  const title = 'SPINs pending'
  return {
    kind: 'pending',
    title,
    detail,
    text: `${title} ${detail}`,
    pendingOrders
  }
}

/**
 * What a SPIN control says when the scope has no SPIN data to show: the pending sentence when clustering is
 * outstanding, else the `none` one — an `available` status cannot explain an empty scope, so it is not trusted here.
 */
export const spinEmptyStateText = (status: SpinScopeStatus | null | undefined, scope: SpinStatusScope): string => {
  const described = describeSpinStatus(status, scope)
  return described.kind === 'pending' ? described.text : describeSpinStatus(undefined, scope).text
}
